import { FC, useEffect, useState } from "react";
import { fetchAsync, postAsync } from "../../services/ApiHandler";
import { Account } from "../../model/Account";
import { Subscription } from "../../model/Subscription";
import { MyPageMenu } from "../../parts/MyPageMenu";
import { StripeCard } from "../../model/StripeCard";
import dayjs, { Dayjs } from "dayjs";
import { SetupIntentResponse } from "../../model/SetupIntentResponse";
import { CardRegistrationDialog, CardRegistrationDialogProps } from "../../parts/CardRegisterDialog";
import { useHeaderStore } from "../../parts/CommonHeader";
import { getExpiryText, getNextPlanId, getPlanNameBySubscription, getPlanPriceText, getRnewwalType, getTransactionItemName } from "../../services/SubscriptionService";
import { SubscriptionDialog, SubscriptionDialogProps } from "../../parts/SubscriptionDialog";
import { useDisclosure } from "@mantine/hooks";
import { Modal } from "@mantine/core";
import { StripeTransaction } from "../../model/StripeTransaction";
import toast, { Toaster } from "react-hot-toast";
import { modals } from '@mantine/modals';
import { Text } from '@mantine/core';
import { Helmet } from "react-helmet";
import { StripePortalResponse } from "../../model/StripePortalResponse";

export const SubscriptionPage: FC = () => {
  const { subscription, remainCount, setSubscription } = useHeaderStore();
  const [account, setAccount] = useState<Account | undefined>(undefined);
  const [cardList, setCardList] = useState<StripeCard[] | undefined>(undefined);
  const [card, setCard] = useState<StripeCard | undefined>(undefined);
  const [transactions, setTransactions] = useState<StripeTransaction[] | undefined>(undefined);
  const [cardProps, setCardProps] = useState<CardRegistrationDialogProps | undefined>(undefined);
  const [subscriptionDialogOpened, { open: openSubscriptionDialog, close: closeSubscriptionDialog }] = useDisclosure(false);
  const [subscriptionDialogProps, setSubscriptionDialogProps] = useState<SubscriptionDialogProps | undefined>(undefined);
  const [isAutoRenewalDialogOpened, { open: openIsAutoRenewalDialog, close: closeIsAutoRenewalDialog }] = useDisclosure(false);
  const [subscriptionCancelDialogOpened, { open: openSubscriptionCancelDialog, close: closeSubscriptionCancelDialog }] = useDisclosure(false);
  const [isProcessing, setIsProcessing] = useState(false);

  function onClickUpgradePlan() {
    const nextPlanId = getNextPlanId(subscription);
    setSubscriptionDialogProps({
      planId: nextPlanId
    } as SubscriptionDialogProps);
    openSubscriptionDialog();
  }

  async function openStripePortal() {
    setIsProcessing(true);
    const response = await fetchAsync<StripePortalResponse>('Stripe/GetPortalSessionUrl');
    window.location.href = response.url;
    // window.open(response.url, '_blank');
    setIsProcessing(false);
  }

  const fetchAccount = async () => {
    const response = await fetchAsync<Account>('Account/GetAccountInfo');
    setAccount(response);
  };

  useEffect(() => {
    const fetchSubscription = async () => {
      const response = await fetchAsync<Subscription>('Subscription/GetActiveSubscription');
      setSubscription(response);
    };

    const fetchCard = async () => {
      const response = await fetchAsync<StripeCard[]>('Stripe/GetActiveCard');
      setCardList(response);
      if(response.length >= 1) {
        setCard(response[0]);
      }
    };

    const fetchSubscriptionList = async () => {
      const response = await fetchAsync<StripeTransaction[]>('Stripe/GetTransactions');
      setTransactions(response);
    };

    fetchAccount();
    fetchSubscription();
    fetchCard();
    fetchSubscriptionList();
  }, [])

  function getCardImageUrl(brand: string) : string {
    return `/images/icon/card-${brand}.png`;
  }

  async function onClickCancelAutoRenewal() {
    var response = await postAsync<string>('Stripe/CancelSubscription', { subscriptionId: subscription?.subscriptionId });
    fetchAccount();
    if(response.code === 1) {
      closeSubscriptionCancelDialog();
      toast.success("自動更新キャンセルしました");
    } else {
      toast.error(response.message ?? "");
    }
  }

  function getTransactionItemName(itemName: string, price: number) : string {
    return price === 0 ? `${itemName}(トライアル)` : `${itemName}`;
  }

  return <>
    <Helmet>
      <title>サブスクリプション - Drop AI</title>
      <meta name="description" content="お客さまのサブスクリプション契約状況を確認できます。" />

      <meta property="og:url" content="https://drop4i.com/subscription" />
      <meta property="og:type" content="website" />
      <meta property="og:title" content="サブスクリプション - Drop AI | 無料で使えるAI画像生成ツール" />
      <meta property="og:description" content="お客さまのサブスクリプション契約状況を確認できます。" />
    </Helmet>
    <Toaster />
    { cardProps !== undefined &&
      <CardRegistrationDialog clientSecret={cardProps?.clientSecret!} onClose={() => setCardProps(undefined)} />
    }
    { subscriptionCancelDialogOpened &&
      <Modal size="lg" opened={subscriptionCancelDialogOpened} onClose={closeSubscriptionCancelDialog}>
        <div>自動更新をキャンセル</div>
        <div className="mt-4">自動更新をキャンセルすると、プランの有効期限経過後はFreeプランにダウングレードされます。</div>
        <div className="mt-4">[主な変更点]</div>
        <div className="text-sm">生成回数制限が1日単位に変更 / 同時生成数が1件に変更 / 広告非表示オプションなし / 生成画像の保存期間が30日間に短縮 / 詳細画像設定機能なし</div>
        <div className="text-center mt-6">
          <a href="/plan" className="text-[#0909FF] underline text-sm">料金プランを確認する</a>
        </div>
        <div className="text-center mt-4">
          <button onClick={closeSubscriptionCancelDialog} className="text-white px-4 py-2 rounded-lg bg-[#0909FF] w-[200px]">閉じる</button>
        </div>
        <div className="text-center mt-4 mb-6"> 
          <button onClick={onClickCancelAutoRenewal} className="text-[#0909FF] px-4 py-2 rounded-lg bg-[#EAEAFF] w-[200px]">自動更新をキャンセル</button>      
        </div>
      </Modal>
    }
    { subscriptionDialogProps !== undefined &&
      <Modal size="auto" opened={subscriptionDialogOpened} onClose={closeSubscriptionDialog}>
        <SubscriptionDialog onPurchase={() => setSubscriptionDialogProps(undefined)} onCancel={() => setSubscriptionDialogProps(undefined)} planId={subscriptionDialogProps?.planId} />
      </Modal>
    }
     <div className="min-h-[calc(100vh-76px)] pb-20" style={{backgroundColor: '#eaeaea80'}}>
        <div className="pt-0 sm:pt-1"></div>
        <div className="mt-0 sm:mt-10 mx-auto max-w-[800px] bg-white rounded-lg py-4 px-6"> 
        <div className="text-4xl font-bold">お支払い</div>
        <div className="mt-4 py-4 px-1 sm:px-4 rounded-lg bg-white sm:bg-[#eaeaea80]">
          <div>
            <div className="text-lg font-bold">現在のプラン</div>
            <div className="flex items-center">
              <span className="text-lg sm:text-2xl font-bold w-[200px] inline-block">{getPlanNameBySubscription(subscription)}</span>
              <span className="text-lg sm:text-2xl font-bold">{getPlanPriceText(subscription)}</span>
              { (subscription?.internalProductId === 'free' || subscription?.internalProductId.startsWith('lite')) &&
                <span onClick={onClickUpgradePlan} className="hidden sm:block clickable gradation-button font-bold px-4 py-2 rounded-lg ml-auto">プランをアップグレード</span>
              }
            </div>
            <div className="block sm:hidden mt-2">
              { (subscription?.internalProductId === 'free' || subscription?.internalProductId.startsWith('lite')) &&
                <span onClick={onClickUpgradePlan} className="block clickable w-full gradation-button font-bold px-4 py-2 rounded-lg ml-auto">プランをアップグレード</span>
              }
            </div>
            <div className="flex items-center mt-6">
              <img src="/images/icon/icon-files.svg" className="w-[16px] h-[16px] mr-1 inline-block" />
              <span className="text-lg font-bold w-[180px] inline-block">残りの生成回数</span>
              <span>{remainCount} 回</span>
            </div>
            <div className="flex items-center mt-2">
              <img src="/images/icon/icon-pencil.svg" className="w-[16px] h-[16px] mr-1 inline-block" />
              <span className="text-lg font-bold w-[180px] inline-block">契約種別</span>
              <span>{getRnewwalType(subscription)}</span>
            </div>
            <div className="flex items-center mt-2">
              <img src="/images/icon/icon-watch.svg" className="w-[16px] h-[16px] mr-1 inline-block" />
              <span className="text-lg font-bold w-[180px] inline-block">プランの有効期限</span>
              <span>{getExpiryText(subscription)}</span>
            </div>
          </div>
          
        </div>
        <div className="mt-8">
          <div className="flex items-center justify-between">
            <span className="text-lg font-bold">お支払い方法</span>
          </div>
          <div>
            <button disabled={isProcessing} onClick={() => openStripePortal()} className=" border rounded py-1 px-4 hover-button">お支払いの詳細を確認する</button>
          </div>
        </div>
        <div className="mt-8">
          <div className="text-lg font-bold">お支払い履歴</div>
          { (transactions !== undefined && transactions.length === 0) &&
            <div>お支払い履歴はありません。</div>
          }
          { (transactions !== undefined && transactions.length >= 1) &&
            <>
            <table className="basic-table w-full py-2">
              <tr className="border-b border-gray-300">
                <th className="py-2">日付</th>
                <th className="py-2">説明</th>
                <th className="py-2">合計</th>
              </tr>
            { transactions!.map((e) => (
              <>
                <tr className="border-b border-gray-300">
                <td className="py-2">{dayjs(e.createdDate).format('YYYY-M-D')}</td>
                  <td className="py-2">{ getTransactionItemName(e.productName, e.amount) }</td>
                  <td className="py-2">{e.amount} 円</td>
                </tr>
              </>
            ))}
            </table>
            </>
          }
        </div>
        <div className="mt-8 text-sm mb-4">
          <div>
          ※アップグレードについて：「お支払いページ」「料金プランページ」よりプラン変更が可能です。
          </div>
          <div className="mt-2">
            ※ダウングレードについて：お支払いの詳細より「サブスクリプション更新のキャンセル」によってFreeプランへのダウングレードが可能です。StandardプランからLiteプランへの変更をご希望される場合は、「サブスクリプション更新のキャンセル」によって一度Freeプランにダウングレードした後に、改めてLiteプランをご契約くださいませ。
          </div>
          <div className="mt-2">
            ※料金プラン変更の詳細については<a href="/article/help/about-plan" className="underline text-[#0909FF]">ヘルプページ</a>をご確認ください。
          </div>
        </div>
      </div>
    </div>
  </>;
}
  